import React, { useState, useCallback, useRef } from 'react';
import {
  Upload, Download, Loader2, AlertCircle, Eraser, RotateCcw,
  Eye, Palette, RefreshCw
} from 'lucide-react';
import { API_CONFIG, formatFileSize } from '../config/api';

type BgMode = 'transparent' | 'white' | 'color' | 'blur';

const RemoveBackground: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [originalPreview, setOriginalPreview] = useState<string | null>(null);
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [compositeUrl, setCompositeUrl] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bgMode, setBgMode] = useState<BgMode>('transparent');
  const [customColor, setCustomColor] = useState('#3b82f6');
  const [downloadFormat, setDownloadFormat] = useState<'png' | 'jpg'>('png');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const handleFileSelect = useCallback((selectedFiles: FileList | null) => {
    if (!selectedFiles || selectedFiles.length === 0) return;
    const f = selectedFiles[0];
    if (!f.type.startsWith('image/')) {
      setError('Please select an image file.');
      return;
    }
    setFile(f);
    setError(null);
    setResultUrl(null);
    setCompositeUrl(null);
    setBgMode('transparent');
    const reader = new FileReader();
    reader.onload = (e) => setOriginalPreview(e.target?.result as string);
    reader.readAsDataURL(f);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    handleFileSelect(e.dataTransfer.files);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const processImage = async () => {
    if (!file) return;
    setIsProcessing(true);
    setError(null);
    setResultUrl(null);
    setCompositeUrl(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_CONFIG.BASE_URL}/imagetool/remove-background`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Background removal failed');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      setResultUrl(url);
      setCompositeUrl(url);
    } catch (err) {
      if (err instanceof TypeError && err.message.includes('fetch')) {
        setError('Background removal service is temporarily unavailable. Please try again later.');
      } else {
        setError(err instanceof Error ? err.message : 'Background removal failed. Please try again.');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const applyBackground = useCallback((mode: BgMode) => {
    setBgMode(mode);

    if (!resultUrl || !originalPreview) return;

    if (mode === 'transparent') {
      setCompositeUrl(resultUrl);
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    const resultImg = new window.Image();
    resultImg.onload = () => {
      canvas.width = resultImg.naturalWidth;
      canvas.height = resultImg.naturalHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      if (mode === 'white') {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(resultImg, 0, 0);
        canvas.toBlob((blob) => {
          if (blob) setCompositeUrl(URL.createObjectURL(blob));
        }, 'image/png');
      } else if (mode === 'color') {
        ctx.fillStyle = customColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(resultImg, 0, 0);
        canvas.toBlob((blob) => {
          if (blob) setCompositeUrl(URL.createObjectURL(blob));
        }, 'image/png');
      } else if (mode === 'blur') {
        const origImg = new window.Image();
        origImg.onload = () => {
          ctx.filter = 'blur(20px)';
          // Draw slightly larger to avoid blur edge artifacts
          ctx.drawImage(origImg, -20, -20, canvas.width + 40, canvas.height + 40);
          ctx.filter = 'none';
          ctx.drawImage(resultImg, 0, 0);
          canvas.toBlob((blob) => {
            if (blob) setCompositeUrl(URL.createObjectURL(blob));
          }, 'image/png');
        };
        origImg.src = originalPreview;
      }
    };
    resultImg.src = resultUrl;
  }, [resultUrl, originalPreview, customColor]);

  // Re-apply when custom color changes
  const handleColorChange = useCallback((color: string) => {
    setCustomColor(color);
    if (bgMode === 'color' && resultUrl) {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const img = new window.Image();
      img.onload = () => {
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.fillStyle = color;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        canvas.toBlob((blob) => {
          if (blob) setCompositeUrl(URL.createObjectURL(blob));
        }, 'image/png');
      };
      img.src = resultUrl;
    }
  }, [bgMode, resultUrl]);

  const downloadResult = () => {
    if (!compositeUrl || !file) return;

    if (downloadFormat === 'png') {
      const link = document.createElement('a');
      link.href = compositeUrl;
      const name = file.name.replace(/\.[^.]+$/, '');
      link.download = `${name}_no_bg.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      // Convert to JPG with white/chosen background
      const canvas = canvasRef.current;
      if (!canvas) return;
      const img = new window.Image();
      img.onload = () => {
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        if (bgMode === 'transparent' || bgMode === 'white') {
          ctx.fillStyle = '#ffffff';
        } else if (bgMode === 'color') {
          ctx.fillStyle = customColor;
        } else {
          ctx.fillStyle = '#ffffff';
        }
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        canvas.toBlob((blob) => {
          if (blob) {
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            const name = file.name.replace(/\.[^.]+$/, '');
            link.download = `${name}_no_bg.jpg`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
        }, 'image/jpeg', 0.95);
      };
      img.src = compositeUrl;
    }
  };

  const reset = () => {
    setFile(null);
    setOriginalPreview(null);
    setResultUrl(null);
    setCompositeUrl(null);
    setError(null);
    setBgMode('transparent');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const checkerboardStyle: React.CSSProperties = {
    backgroundImage: `linear-gradient(45deg, #e5e7eb 25%, transparent 25%),
      linear-gradient(-45deg, #e5e7eb 25%, transparent 25%),
      linear-gradient(45deg, transparent 75%, #e5e7eb 75%),
      linear-gradient(-45deg, transparent 75%, #e5e7eb 75%)`,
    backgroundSize: '16px 16px',
    backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0px',
  };

  return (
    <div className="space-y-6">
      <canvas ref={canvasRef} className="hidden" />

      {/* Header */}
      <div className="text-center">
        <div className="flex justify-center mb-4">
          <div className="p-3 bg-purple-100 rounded-full">
            <Eraser className="w-8 h-8 text-purple-600" />
          </div>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Remove Background</h2>
        <p className="text-gray-600 max-w-lg mx-auto">
          Automatically remove the background from any image. Replace with transparent, solid color, or blurred original.
        </p>
      </div>

      {/* Upload Area */}
      {!file && (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-purple-400 transition-colors cursor-pointer"
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Drop an image here or click to select
          </h3>
          <p className="text-gray-500 text-sm">
            Supports PNG, JPG, WebP up to {formatFileSize(API_CONFIG.MAX_FILE_SIZE)}
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={(e) => handleFileSelect(e.target.files)}
            className="hidden"
          />
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-red-700">{error}</p>
            <button
              onClick={processImage}
              className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-red-600 hover:text-red-800 transition-colors"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      {file && (
        <div className="space-y-4">
          {/* File info */}
          <div className="bg-gray-50 rounded-xl p-3 flex items-center gap-3 text-sm">
            <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-200 flex-shrink-0">
              {originalPreview && <img src={originalPreview} alt="" className="w-full h-full object-cover" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-gray-900 truncate">{file.name}</p>
              <p className="text-xs text-gray-500">{formatFileSize(file.size)} - {file.type.split('/')[1]?.toUpperCase()}</p>
            </div>
          </div>

          {/* Side by side comparison */}
          <div className="grid md:grid-cols-2 gap-4">
            {/* Original */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <Eye className="w-4 h-4" /> Original
              </h3>
              <div className="bg-gray-100 rounded-xl overflow-hidden flex items-center justify-center min-h-[250px] max-h-[450px]">
                {originalPreview && (
                  <img src={originalPreview} alt="Original" className="max-w-full max-h-[450px] object-contain" />
                )}
              </div>
            </div>

            {/* Result */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <Eraser className="w-4 h-4" /> Result
              </h3>
              <div
                className="rounded-xl overflow-hidden flex items-center justify-center min-h-[250px] max-h-[450px]"
                style={bgMode === 'transparent' && compositeUrl ? checkerboardStyle : {
                  backgroundColor: bgMode === 'white' ? '#ffffff' :
                    bgMode === 'color' ? customColor : '#f3f4f6'
                }}
              >
                {isProcessing ? (
                  <div className="flex flex-col items-center py-16">
                    <Loader2 className="w-10 h-10 text-purple-600 animate-spin mb-3" />
                    <span className="text-gray-600 font-medium">Removing background...</span>
                    <span className="text-gray-400 text-sm mt-1">This may take a few seconds</span>
                  </div>
                ) : compositeUrl ? (
                  <img src={compositeUrl} alt="Result" className="max-w-full max-h-[450px] object-contain" />
                ) : (
                  <div className="text-gray-400 py-16 bg-gray-50 w-full text-center">
                    <Eraser className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>Click "Remove Background" to process</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Background options (shown after result) */}
          {resultUrl && (
            <div className="bg-gray-50 rounded-xl p-4">
              <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                <Palette className="w-4 h-4" /> Background Options
              </h4>
              <div className="flex flex-wrap gap-2">
                {([
                  ['transparent', 'Transparent', checkerboardStyle],
                  ['white', 'White', { backgroundColor: '#ffffff' }],
                  ['color', 'Custom Color', { backgroundColor: customColor }],
                  ['blur', 'Blurred Original', { backgroundColor: '#e5e7eb' }],
                ] as [BgMode, string, React.CSSProperties][]).map(([mode, label, style]) => (
                  <button
                    key={mode}
                    onClick={() => applyBackground(mode)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      bgMode === mode
                        ? 'bg-purple-600 text-white'
                        : 'bg-white border border-gray-200 text-gray-700 hover:border-purple-300'
                    }`}
                  >
                    <div
                      className="w-4 h-4 rounded border border-gray-300"
                      style={bgMode === mode ? { ...style, borderColor: 'rgba(255,255,255,0.3)' } : style}
                    />
                    {label}
                  </button>
                ))}
                {bgMode === 'color' && (
                  <input
                    type="color"
                    value={customColor}
                    onChange={(e) => handleColorChange(e.target.value)}
                    className="w-9 h-9 rounded-lg border border-gray-300 cursor-pointer"
                  />
                )}
              </div>

              {/* Download format */}
              <div className="mt-3 pt-3 border-t border-gray-200 flex items-center gap-3">
                <span className="text-sm text-gray-600">Download as:</span>
                <div className="flex gap-2">
                  {(['png', 'jpg'] as const).map(fmt => (
                    <button
                      key={fmt}
                      onClick={() => setDownloadFormat(fmt)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        downloadFormat === fmt
                          ? 'bg-purple-100 text-purple-700 border border-purple-200'
                          : 'bg-white border border-gray-200 text-gray-600 hover:border-purple-200'
                      }`}
                    >
                      {fmt.toUpperCase()} {fmt === 'png' ? '(transparency)' : '(with background)'}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3">
            {!resultUrl && (
              <button
                onClick={processImage}
                disabled={isProcessing}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-purple-600 text-white font-medium rounded-xl hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Eraser className="w-5 h-5" />
                    Remove Background
                  </>
                )}
              </button>
            )}

            {resultUrl && (
              <button
                onClick={downloadResult}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white font-medium rounded-xl hover:bg-green-700 transition-colors"
              >
                <Download className="w-5 h-5" />
                Download {downloadFormat.toUpperCase()}
              </button>
            )}

            <button
              onClick={reset}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-200 text-gray-700 font-medium rounded-xl hover:bg-gray-300 transition-colors"
            >
              <RotateCcw className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RemoveBackground;
